import { Link } from "react-router-dom";
import { Heart, ShoppingCart } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { rp, fmtDate } from "@/lib/format";
import { useShop } from "@/lib/shop";
import { PoBadge, Pill, DemoBadge, CondBadges } from "@/components/site/Badges";

export function cardMode(p) {
  if (p.sale_type === "po") return "po";
  if (p.sale_type === "both" && !p.ready_orderable) return "po";
  return "ready";
}

function PriceBlock({ p, mode }) {
  if (mode === "po" && p.po_info) {
    const po = p.po_info;
    return (
      <div>
        <div className="price text-lg font-bold text-neutral-900">{rp(po.price)}</div>
        {po.normal_price && po.normal_price > po.price && <div className="text-xs text-neutral-400 line-through">{rp(po.normal_price)}</div>}
        <div className="text-[11.5px] text-neutral-600 mt-0.5">
          {po.dp_per_unit ? `DP ${rp(po.dp_per_unit)}` : "Tanpa DP"}
          {po.close_at && ` · tutup ${fmtDate(po.close_at)}`}
        </div>
      </div>
    );
  }
  const pr = p.pricing;
  return (
    <div>
      <div className={`price text-lg font-bold ${pr.discount_pct ? "text-[var(--bk-red)]" : "text-neutral-900"}`}>{rp(pr.final_price)}</div>
      {pr.discount_pct > 0 && <div className="text-xs text-neutral-400 line-through">{rp(pr.price)}</div>}
    </div>
  );
}

export default function ProductCard({ p, forcePo = false }) {
  const { addToCart, toggleFav, isFav } = useShop();
  const mode = forcePo ? "po" : cardMode(p);
  const href = mode === "po" ? `/pre-order/${p.slug}` : `/produk/${p.slug}`;
  const hasVariants = (p.variants || []).length > 0;
  const orderable = mode === "po" ? p.po_info?.orderable : p.ready_orderable;
  const fav = isFav(p.id);
  return (
    <div data-testid={`product-card-${p.slug}`} className="group relative flex flex-col rounded-xl border border-neutral-200 bg-white overflow-hidden transition-shadow duration-200 hover:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.25)]">
      <Link to={href} className="relative block aspect-square bg-neutral-50 overflow-hidden" data-testid={`product-card-image-link-${p.slug}`}>
        <img src={imgUrl(p.images?.[0])} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-2 top-2 flex flex-col items-start gap-1">
          {mode === "po" ? <PoBadge status={p.po_info?.status} /> : p.stock > 0 ? <Pill cls="bg-[var(--bk-yellow)] text-neutral-900 border border-neutral-900">Ready</Pill> : <Pill cls="bg-neutral-800 text-white">Stok Habis</Pill>}
          {mode === "ready" && p.pricing.discount_pct > 0 && <Pill cls="bg-[var(--bk-red)] text-white">-{p.pricing.discount_pct}%</Pill>}
          {p.is_demo && <DemoBadge />}
          <CondBadges p={p} />
        </div>
      </Link>
      <button
        data-testid={`fav-toggle-${p.slug}`}
        aria-label={fav ? "Hapus dari favorit" : "Tambah ke favorit"}
        onClick={() => toggleFav(p.id)}
        className={`absolute right-2 top-2 h-9 w-9 grid place-items-center rounded-full border-2 border-neutral-900 transition-colors ${fav ? "bg-[var(--bk-red)] text-white" : "bg-white text-neutral-900 hover:bg-[var(--bk-yellow)]"}`}
      >
        <Heart className="h-4 w-4" fill={fav ? "currentColor" : "none"} />
      </button>
      <div className="flex flex-1 flex-col p-3 sm:p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--bk-blue)] truncate">{p.brand || "—"}</div>
        <Link to={href} className="mt-0.5 font-heading text-[15px] font-semibold leading-snug line-clamp-2 min-h-[2.6em] hover:underline">
          {p.name}
        </Link>
        <div className="mt-2 flex-1">
          <PriceBlock p={p} mode={mode} />
        </div>
        <div className="mt-3 flex gap-2">
          <Link to={href} data-testid={`product-detail-btn-${p.slug}`} className="flex-1 h-10 inline-flex items-center justify-center rounded-lg border-2 border-neutral-900 text-sm font-semibold hover:bg-neutral-900 hover:text-white transition-colors">
            Detail
          </Link>
          {orderable && !hasVariants && (
            <button
              data-testid={`add-to-cart-${p.slug}`}
              aria-label="Tambah ke keranjang"
              onClick={() => addToCart(p, mode)}
              className="h-10 w-11 grid place-items-center rounded-lg bg-[var(--bk-yellow)] border-2 border-neutral-900 hover:bg-[var(--bk-orange)] transition-colors"
            >
              <ShoppingCart className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const ProductGrid = ({ items, forcePo }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
    {items.map((p) => (
      <ProductCard key={p.id} p={p} forcePo={forcePo} />
    ))}
  </div>
);
